import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import * as XLSX from "xlsx";
import "./DSTrungThu.css";
import NavbarTrungThu from "../Layout/NavbarTrungThu";

const normalizeText = (value) => {
  if (value == null) return "";
  return String(value).toLowerCase();
};

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const DSTrungThu = () => {
  const [data, setData] = useState([]);
  const [importData, setImportData] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [auditData, setAuditData] = useState([]); // Add state for audit data
  const [summary, setSummary] = useState({});
  const [importSummary, setImportSummary] = useState({});
  const [distributionData, setDistributionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Add state for expanded rows - now supporting three levels
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [expandedStores, setExpandedStores] = useState(new Set());
  const [expandedBatches, setExpandedBatches] = useState(new Set());

  // Add state for stores
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [storeSearchTerm, setStoreSearchTerm] = useState("");
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);

  // Add state for multi-store data
  const [multiStoreData, setMultiStoreData] = useState({});
  const [isAllStoresMode, setIsAllStoresMode] = useState(true);

  // Add search and filter states
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [filterInStock, setFilterInStock] = useState(false);
  const [filterNotExpired, setFilterNotExpired] = useState(false);

  // Filter states - updated to use date range
  const [maCH, setMaCH] = useState("ALL");
  const [selectedStore, setSelectedStore] = useState(null);
  const [ngayBatDau, setNgayBatDau] = useState("2025-08-26");
  const today = new Date().toISOString().split("T")[0];
  const [ngayKetThuc, setNgayKetThuc] = useState(today);

  // Add state for new expiry filters (after existing filter states)
  const [filterExpiringDays, setFilterExpiringDays] = useState(null);
  const [expiringDaysInput, setExpiringDaysInput] = useState("");

  // Fetch stores data
  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stores`);
      if (response.data && Array.isArray(response.data)) {
        // Filter stores with LoaiCH 2 and 0, and handle special case for IDCH = 20
        const validStores = response.data
          .filter(
            (store) =>
              store.LoaiCH === 2 || store.LoaiCH === 0 || store.IDCH === 20
          )
          .map((store) => ({
            ...store,
            // Set special display name for store 20
            TenCuaHang: store.IDCH === 20 ? "Kho tổng" : store.TenCuaHang,
            totalInventory: 0, // Add total inventory property
          }));
        setStores(validStores);
        setFilteredStores([
          { IDCH: "ALL", TenCuaHang: "Tất cả cửa hàng", totalInventory: 0 },
          ...validStores,
        ]);

        // Set default store if current maCH exists in stores
        const defaultStore = validStores.find(
          (store) => store.IDCH.toString() === maCH
        );
        if (defaultStore) {
          setSelectedStore(defaultStore);
          setStoreSearchTerm(
            `${defaultStore.IDCH} - ${defaultStore.TenCuaHang}`
          );
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
    }
  };

  // Handle store search
  const handleStoreSearch = (searchTerm) => {
    setStoreSearchTerm(searchTerm);
    const allStoresOption = { IDCH: "ALL", TenCuaHang: "Tất cả cửa hàng", totalInventory: 0 };
    const searchTermLower = normalizeText(searchTerm);
    const filtered = [
      allStoresOption,
      ...stores.filter((store) => {
        const storeIdText = normalizeText(store?.IDCH?.toString());
        const storeNameText = normalizeText(store?.TenCuaHang);
        return (
          storeIdText.includes(searchTermLower) ||
          storeNameText.includes(searchTermLower)
        );
      }),
    ];
    setFilteredStores(filtered);
    setShowStoreDropdown(true);
  };

  // Handle store selection
  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    if (store.IDCH === "ALL") {
      setMaCH("ALL");
      setIsAllStoresMode(true);
      setStoreSearchTerm("Tất cả cửa hàng");
    } else {
      setMaCH(store.IDCH.toString());
      setIsAllStoresMode(false);
      setStoreSearchTerm(`${store.IDCH} - ${store.TenCuaHang}`);
    }
    setShowStoreDropdown(false);
  };

  // Get Bakery store IDs
  const getBakeryStoreIds = () => {
    return stores
      .filter((store) => normalizeText(store?.TenCuaHang).includes("bakery"))
      .map((store) => store.IDCH.toString());
  };

  const fetchSalesData = async () => {
    const storeIds = isAllStoresMode ? getBakeryStoreIds().join(",") : maCH;
    const response = await axios.get(
      `${API_BASE_URL}/api/btt-ledger/sales`,
      {
        params: {
          MaCH: storeIds,
          NgayBatDau: ngayBatDau,
          NgayKetThuc: ngayKetThuc,
        },
      }
    );
    return response.data;
  };

  const fetchImportData = async () => {
    const storeIds = isAllStoresMode ? getBakeryStoreIds().join(",") : maCH;
    const response = await axios.get(
      `${API_BASE_URL}/api/btt-ledger/imports`,
      {
        params: {
          MaCH: storeIds,
          NgayBatDau: ngayBatDau,
          NgayKetThuc: ngayKetThuc,
        },
      }
    );
    return response.data;
  };

  // Add new function to fetch inventory data
  const fetchInventoryData = async () => {
    const storeIds = isAllStoresMode ? getBakeryStoreIds().join(",") : maCH;
    const response = await axios.get(
      `${API_BASE_URL}/api/btt-ledger/inventory`,
      {
        params: {
          MaCH: storeIds,
          NgayTruyVan: ngayKetThuc,
        },
      }
    );
    return response.data;
  };

  // Add new function to fetch sales receipts data
  const fetchSalesReceiptsData = async (storeIds) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/btt-ledger/sales-receipts`,
        {
          params: {
            MaCH: storeIds,
            NgayBatDau: ngayBatDau,
            NgayKetThuc: ngayKetThuc,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching sales receipts:", error);
      throw error;
    }
  };

  // Add new function to fetch inventory audit data
  const fetchAuditData = async () => {
    const storeIds = isAllStoresMode ? getBakeryStoreIds().join(",") : maCH;
    const response = await axios.get(
      `${API_BASE_URL}/api/btt-ledger/audit`,
      {
        params: {
          MaCH: storeIds,
          NgayBatDau: ngayBatDau,
          NgayKetThuc: ngayKetThuc,
        },
      }
    );
    return response.data;
  };

  // Group multi-store data by product and store
  const groupMultiStoreData = (
    salesData,
    importData,
    inventoryData,
    auditData = {}
  ) => {
    const productMap = new Map();

    // Create inventory lookup maps
    const inventoryByStore = new Map();
    const inventoryTenSPByStore = new Map();
    const inventoryPhanBoByStore = new Map(); // THÊM MỚI

    if (inventoryData.dataByStore) {
      Object.entries(inventoryData.dataByStore).forEach(
        ([storeId, storeInventory]) => {
          inventoryByStore.set(storeId, new Map());
          inventoryTenSPByStore.set(storeId, new Map());
          inventoryPhanBoByStore.set(storeId, new Map()); // THÊM MỚI

          storeInventory.forEach((item) => {
            const key = `${item.MaSP}-${item.LoSX}`;
            inventoryByStore.get(storeId).set(key, item.TonConLai);
            inventoryTenSPByStore.get(storeId).set(item.MaSP, item.Ten_SP);

            // THÊM MỚI: Lưu thông tin phân bổ cho store 20
            if (storeId === "20" && item.SoLuongPhanBo) {
              inventoryPhanBoByStore.get(storeId).set(key, item.SoLuongPhanBo);
            }
          });
        }
      );
    }
    // Create audit lookup maps
    const auditByStore = new Map();
    if (auditData.dataByStore) {
      Object.entries(auditData.dataByStore).forEach(([storeId, storeAudit]) => {
        auditByStore.set(storeId, new Map());
        storeAudit.forEach((item) => {
          const key = `${item.MaSP}-${item.LoSX}`;
          auditByStore.get(storeId).set(key, item.SoLuongKiemKe);
        });
      });
    }

    // Process import data by store
    if (importData.dataByStore) {
      Object.entries(importData.dataByStore).forEach(([storeId, storeData]) => {
        storeData.forEach((item) => {
          const productKey = item.MaSP;
          if (!productMap.has(productKey)) {
            productMap.set(productKey, {
              MaSP: item.MaSP,
              Ten_SP:
                (inventoryTenSPByStore.get(storeId) &&
                  inventoryTenSPByStore.get(storeId).get(item.MaSP)) ||
                item.Ten_SP,
              DVT_SP: item.DVT_SP,
              totalSoLuongNhap: 0,
              totalSoLuongBan: 0,
              totalSoLuongDieuChuyen: 0,
              totalSoLuongPhanBo: 0,
              totalSoLuongTraVe: 0,
              totalSoLuongKiemKe: 0, // Add audit total
              totalTonKho: 0,
              totalThanhTienNhap: 0,
              totalThanhTienBan: 0,
              storeCount: 0,
              totalStores: new Set(),
              storeData: new Map(),
            });
          }

          const product = productMap.get(productKey);

          if (!product.storeData.has(storeId)) {
            product.storeData.set(storeId, {
              storeId: storeId,
              storeName:
                stores.find((s) => s.IDCH.toString() === storeId)?.TenCuaHang ||
                `Cửa hàng ${storeId}`,
              maBravo: stores.find((s) => s.IDCH.toString() === storeId)?.MaBravo || "",
              SoLuongNhap: 0,
              SoLuongBan: 0,
              SoLuongDieuChuyen: 0,
              SoLuongPhanBo: 0,
              SoLuongTraVe: 0,
              SoLuongKiemKe: 0, // Add audit quantity
              TonKho: 0,
              ThanhTienNhap: 0,
              ThanhTienBan: 0,
              batches: new Map(),
            });
            product.totalStores.add(storeId);
          }

          const storeInfo = product.storeData.get(storeId);
          storeInfo.SoLuongNhap += item.SoLuongNhap || 0;
          storeInfo.ThanhTienNhap += item.TongThanhTien || 0;

          product.totalSoLuongNhap += item.SoLuongNhap || 0;
          product.totalThanhTienNhap += item.TongThanhTien || 0;

          // Add batch data
          const batchKey = item.LoSX;
          if (!storeInfo.batches.has(batchKey)) {
            storeInfo.batches.set(batchKey, {
              LoSX: item.LoSX,
              NgaySX: item.NgaySX,
              NgaySD: item.NgaySD,
              DonGiaLoSX: item.DonGiaLoSX,
              SoLuongNhap: 0,
              SoLuongBan: 0,
              SoLuongDieuChuyen: 0,
              SoLuongPhanBo: 0,
              SoLuongTraVe: 0,
              SoLuongKiemKe: 0, // Add audit quantity
              TonKho: 0,
              ThanhTienNhap: 0,
              ThanhTienBan: 0,
            });
          }
          const batch = storeInfo.batches.get(batchKey);
          batch.SoLuongNhap += item.SoLuongNhap || 0;
          batch.ThanhTienNhap += item.TongThanhTien || 0;
        });
      });
    }

    // Process sales data by store
    if (salesData.dataByStore) {
      Object.entries(salesData.dataByStore).forEach(([storeId, storeData]) => {
        storeData.forEach((item) => {
          const productKey = item.MaSP;
          if (!productMap.has(productKey)) {
            productMap.set(productKey, {
              MaSP: item.MaSP,
              Ten_SP:
                (inventoryTenSPByStore.get(storeId) &&
                  inventoryTenSPByStore.get(storeId).get(item.MaSP)) ||
                item.Ten_SP,
              DVT_SP: item.DVT_SP,
              totalSoLuongNhap: 0,
              totalSoLuongBan: 0,
              totalSoLuongDieuChuyen: 0,
              totalSoLuongPhanBo: 0,
              totalSoLuongTraVe: 0,
              totalSoLuongKiemKe: 0, // Add audit total
              totalTonKho: 0,
              totalThanhTienNhap: 0,
              totalThanhTienBan: 0,
              storeCount: 0,
              totalStores: new Set(),
              storeData: new Map(),
            });
          }

          const product = productMap.get(productKey);

          if (!product.storeData.has(storeId)) {
            product.storeData.set(storeId, {
              storeId: storeId,
              storeName:
                stores.find((s) => s.IDCH.toString() === storeId)?.TenCuaHang ||
                `Cửa hàng ${storeId}`,
              SoLuongNhap: 0,
              SoLuongBan: 0,
              SoLuongDieuChuyen: 0,
              SoLuongPhanBo: 0,
              SoLuongTraVe: 0,
              SoLuongKiemKe: 0, // Add audit quantity
              TonKho: 0,
              ThanhTienNhap: 0,
              ThanhTienBan: 0,
              batches: new Map(),
            });
            product.totalStores.add(storeId);
          }

          const storeInfo = product.storeData.get(storeId);
          storeInfo.SoLuongBan += item.SoLuongBan || 0;
          storeInfo.SoLuongDieuChuyen += item.SoLuongDieuChuyen || 0;
          storeInfo.SoLuongTraVe += item.SoLuongTraVe || 0;
          storeInfo.ThanhTienBan += item.TongThanhTien || 0;

          product.totalSoLuongBan += item.SoLuongBan || 0;
          product.totalSoLuongDieuChuyen += item.SoLuongDieuChuyen || 0;
          product.totalSoLuongTraVe += item.SoLuongTraVe || 0;
          product.totalThanhTienBan += item.TongThanhTien || 0;

          // Add batch data
          const batchKey = item.LoSX;
          if (!storeInfo.batches.has(batchKey)) {
            storeInfo.batches.set(batchKey, {
              LoSX: item.LoSX,
              NgaySX: item.NgaySX,
              NgaySD: item.NgaySD,
              DonGiaLoSX: item.DonGiaLoSX,
              SoLuongNhap: 0,
              SoLuongBan: 0,
              SoLuongDieuChuyen: 0,
              SoLuongPhanBo: 0,
              SoLuongTraVe: 0,
              SoLuongKiemKe: 0, // Add audit quantity
              TonKho: 0,
              ThanhTienNhap: 0,
              ThanhTienBan: 0,
            });
          }
          const batch = storeInfo.batches.get(batchKey);
          batch.SoLuongBan += item.SoLuongBan || 0;
          batch.SoLuongDieuChuyen += item.SoLuongDieuChuyen || 0;
          batch.SoLuongTraVe += item.SoLuongTraVe || 0;
          batch.ThanhTienBan += item.TongThanhTien || 0;
        });
      });
    }

    // Process audit data by store
    if (auditData.dataByStore) {
      Object.entries(auditData.dataByStore).forEach(([storeId, storeData]) => {
        storeData.forEach((item) => {
          const productKey = item.MaSP;
          if (productMap.has(productKey)) {
            const product = productMap.get(productKey);
            product.totalSoLuongKiemKe += item.SoLuongKiemKe || 0;

            if (product.storeData.has(storeId)) {
              const storeInfo = product.storeData.get(storeId);
              storeInfo.SoLuongKiemKe += item.SoLuongKiemKe || 0;

              const batchKey = item.LoSX;
              if (storeInfo.batches.has(batchKey)) {
                const batch = storeInfo.batches.get(batchKey);
                batch.SoLuongKiemKe += item.SoLuongKiemKe || 0;
              }
            }
          }
        });
      });
    }

    // Process distribution data for store 20
    if (importData.xuatPhanBoKho && importData.xuatPhanBoKho.data) {
      importData.xuatPhanBoKho.data.forEach((item) => {
        const productKey = item.MaSP;
        if (productMap.has(productKey)) {
          const product = productMap.get(productKey);
          product.totalSoLuongPhanBo += item.SoLuongXuat || 0;

          if (product.storeData.has("20")) {
            const storeInfo = product.storeData.get("20");
            storeInfo.SoLuongPhanBo += item.SoLuongXuat || 0;

            const batchKey = item.LoSX;
            if (storeInfo.batches.has(batchKey)) {
              const batch = storeInfo.batches.get(batchKey);
              batch.SoLuongPhanBo += item.SoLuongXuat || 0;
            }
          }
        }
      });
    }

    // Calculate inventory using API data for each product
    const inventory = Array.from(productMap.values()).map((product) => {
      // Calculate inventory for each store using API data
      const storeArray = Array.from(product.storeData.values()).map(
        (storeInfo) => {
          // Calculate inventory for each batch using API data
          const batchArray = Array.from(storeInfo.batches.values()).map(
            (batch) => {
              // Get actual inventory from API
              const inventoryKey = `${product.MaSP}-${batch.LoSX}`;
              const actualTonKho =
                inventoryByStore.has(storeInfo.storeId) &&
                inventoryByStore.get(storeInfo.storeId).has(inventoryKey)
                  ? inventoryByStore.get(storeInfo.storeId).get(inventoryKey)
                  : 0;
              const actualAuditQty =
                auditByStore.has(storeInfo.storeId) &&
                auditByStore.get(storeInfo.storeId).has(inventoryKey)
                  ? auditByStore.get(storeInfo.storeId).get(inventoryKey)
                  : 0;

              // THÊM MỚI: Get actual phân bổ from inventory API for store 20
              const actualPhanBo =
                inventoryPhanBoByStore.has(storeInfo.storeId) &&
                inventoryPhanBoByStore.get(storeInfo.storeId).has(inventoryKey)
                  ? inventoryPhanBoByStore
                      .get(storeInfo.storeId)
                      .get(inventoryKey)
                  : batch.SoLuongPhanBo;

              return {
                ...batch,
                TonKho: actualTonKho,
                SoLuongKiemKe: actualAuditQty,
                SoLuongPhanBo:
                  storeInfo.storeId === "20"
                    ? actualPhanBo
                    : batch.SoLuongPhanBo, // THÊM MỚI
              };
            }
          );

          // Calculate store inventory from batches
          const storeTonKho = batchArray.reduce(
            (sum, batch) => sum + batch.TonKho,
            0
          );

          return {
            ...storeInfo,
            TonKho: storeTonKho,
            batches: batchArray,
          };
        }
      );

      // Calculate total inventory from all stores
      product.totalTonKho = storeArray.reduce(
        (sum, store) => sum + store.TonKho,
        0
      );

      // Count only stores with positive inventory
      product.storeCount = storeArray.filter(
        (store) => store.TonKho > 0
      ).length;

      return {
        ...product,
        stores: storeArray,
      };
    });

    return inventory;
  };

  // Group products by MaSP and calculate aggregated data
  const groupProductsByMaSP = (
    salesData,
    importData,
    distributionData = [],
    inventoryData,
    auditData = {}
  ) => {
    const productMap = new Map();

    // Create inventory lookup map
    const inventoryLookup = new Map();
    const inventoryTenSPLookup = new Map();
    if (inventoryData.dataByStore && inventoryData.dataByStore[maCH]) {
      inventoryData.dataByStore[maCH].forEach((item) => {
        const key = `${item.MaSP}-${item.LoSX}`;
        inventoryLookup.set(key, item.TonConLai);
        inventoryTenSPLookup.set(item.MaSP, item.Ten_SP);
      });
    }

    // Create audit lookup map
    const auditLookup = new Map();
    if (auditData.dataByStore && auditData.dataByStore[maCH]) {
      auditData.dataByStore[maCH].forEach((item) => {
        const key = `${item.MaSP}-${item.LoSX}`;
        auditLookup.set(key, item.SoLuongKiemKe);
      });
    }

    // First, initialize all products from inventory data
    if (inventoryData.dataByStore && inventoryData.dataByStore[maCH]) {
      inventoryData.dataByStore[maCH].forEach((item) => {
        const key = item.MaSP;
        if (!productMap.has(key)) {
          // Try to get product info from sales or import data
          let productInfo =
            salesData.find((s) => s.MaSP === item.MaSP) ||
            importData.find((i) => i.MaSP === item.MaSP);
          productMap.set(key, {
            MaSP: item.MaSP,
            Ten_SP:
              inventoryTenSPLookup.get(item.MaSP) ||
              productInfo?.Ten_SP ||
              item.Ten_SP ||
              `Sản phẩm ${item.MaSP}`,
            DVT_SP: productInfo?.DVT_SP || "Cái",
            SoLuongNhap: 0,
            SoLuongBan: 0,
            SoLuongDieuChuyen: 0,
            SoLuongPhanBo: 0,
            SoLuongTraVe: 0,
            SoLuongKiemKe: 0,
            TonKho: 0,
            ThanhTienNhap: 0,
            ThanhTienBan: 0,
            batches: new Map(),
          });
        }

        const product = productMap.get(key);

        // THÊM MỚI: Cộng SoLuongPhanBo từ inventory data cho store 20
        if (maCH === "20" && item.SoLuongPhanBo) {
          product.SoLuongPhanBo += item.SoLuongPhanBo;
        }

        // Add batch info from inventory
        const batchKey = item.LoSX;
        if (!product.batches.has(batchKey)) {
          // Try to get batch info from sales or import data
          let batchInfo =
            salesData.find(
              (s) => s.MaSP === item.MaSP && s.LoSX === item.LoSX
            ) ||
            importData.find(
              (i) => i.MaSP === item.MaSP && i.LoSX === item.LoSX
            );

          product.batches.set(batchKey, {
            LoSX: item.LoSX,
            NgaySX: batchInfo?.NgaySX || "",
            NgaySD: batchInfo?.NgaySD || "",
            DonGiaLoSX: batchInfo?.DonGiaLoSX || 0,
            SoLuongNhap: 0,
            SoLuongBan: 0,
            SoLuongDieuChuyen: 0,
            SoLuongPhanBo: item.SoLuongPhanBo || 0, // THÊM MỚI
            SoLuongTraVe: 0,
            SoLuongKiemKe: 0,
            TonKho: item.TonConLai,
            ThanhTienNhap: 0,
            ThanhTienBan: 0,
          });
        } else {
          // THÊM MỚI: Nếu batch đã tồn tại, cộng thêm SoLuongPhanBo
          if (maCH === "20" && item.SoLuongPhanBo) {
            const batch = product.batches.get(batchKey);
            batch.SoLuongPhanBo += item.SoLuongPhanBo;
          }
        }
      });
    }

    // Process sales data
    salesData.forEach((item) => {
      const key = item.MaSP;
      if (!productMap.has(key)) {
        productMap.set(key, {
          MaSP: item.MaSP,
          Ten_SP: inventoryTenSPLookup.get(item.MaSP) || item.Ten_SP,
          DVT_SP: item.DVT_SP,
          SoLuongNhap: 0,
          SoLuongBan: 0,
          SoLuongDieuChuyen: 0,
          SoLuongPhanBo: 0,
          SoLuongTraVe: 0,
          SoLuongKiemKe: 0, // Add audit quantity
          TonKho: 0,
          ThanhTienNhap: 0,
          ThanhTienBan: 0,
          batches: new Map(),
        });
      }
      const product = productMap.get(key);
      product.SoLuongBan += item.SoLuongBan || 0;
      product.SoLuongDieuChuyen += item.SoLuongDieuChuyen || 0;
      product.SoLuongTraVe += item.SoLuongTraVe || 0;
      product.ThanhTienBan += item.TongThanhTien || 0;

      // Add or update batch details
      const batchKey = item.LoSX;
      if (!product.batches.has(batchKey)) {
        product.batches.set(batchKey, {
          LoSX: item.LoSX,
          NgaySX: item.NgaySX,
          NgaySD: item.NgaySD,
          DonGiaLoSX: item.DonGiaLoSX,
          SoLuongNhap: 0,
          SoLuongBan: 0,
          SoLuongDieuChuyen: 0,
          SoLuongPhanBo: 0,
          SoLuongTraVe: 0,
          SoLuongKiemKe: 0, // Add audit quantity
          TonKho: 0,
          ThanhTienNhap: 0,
          ThanhTienBan: 0,
        });
      }
      const batch = product.batches.get(batchKey);
      batch.SoLuongBan += item.SoLuongBan || 0;
      batch.SoLuongDieuChuyen += item.SoLuongDieuChuyen || 0;
      batch.SoLuongTraVe += item.SoLuongTraVe || 0;
      batch.ThanhTienBan += item.TongThanhTien || 0;
    });

    // Process import data
    importData.forEach((item) => {
      const key = item.MaSP;
      if (!productMap.has(key)) {
        productMap.set(key, {
          MaSP: item.MaSP,
          Ten_SP: inventoryTenSPLookup.get(item.MaSP) || item.Ten_SP,
          DVT_SP: item.DVT_SP,
          SoLuongNhap: 0,
          SoLuongBan: 0,
          SoLuongDieuChuyen: 0,
          SoLuongPhanBo: 0,
          SoLuongTraVe: 0,
          SoLuongKiemKe: 0, // Add audit quantity
          TonKho: 0,
          ThanhTienNhap: 0,
          ThanhTienBan: 0,
          batches: new Map(),
        });
      }
      const product = productMap.get(key);
      product.SoLuongNhap += item.SoLuongNhap || 0;
      product.ThanhTienNhap += item.TongThanhTien || 0;

      // Add or update batch details
      const batchKey = item.LoSX;
      if (!product.batches.has(batchKey)) {
        product.batches.set(batchKey, {
          LoSX: item.LoSX,
          NgaySX: item.NgaySX,
          NgaySD: item.NgaySD,
          DonGiaLoSX: item.DonGiaLoSX,
          SoLuongNhap: 0,
          SoLuongBan: 0,
          SoLuongDieuChuyen: 0,
          SoLuongPhanBo: 0,
          SoLuongTraVe: 0,
          SoLuongKiemKe: 0, // Add audit quantity
          TonKho: 0,
          ThanhTienNhap: 0,
          ThanhTienBan: 0,
        });
      }
      const batch = product.batches.get(batchKey);
      batch.SoLuongNhap += item.SoLuongNhap || 0;
      batch.ThanhTienNhap += item.TongThanhTien || 0;
    });

    // Process audit data
    if (auditData.dataByStore && auditData.dataByStore[maCH]) {
      auditData.dataByStore[maCH].forEach((item) => {
        const key = item.MaSP;
        if (!productMap.has(key)) {
          productMap.set(key, {
            MaSP: item.MaSP,
            Ten_SP: inventoryTenSPLookup.get(item.MaSP) || item.Ten_SP,
            DVT_SP: item.DVT_SP,
            SoLuongNhap: 0,
            SoLuongBan: 0,
            SoLuongDieuChuyen: 0,
            SoLuongPhanBo: 0,
            SoLuongTraVe: 0,
            SoLuongKiemKe: 0, // Add audit quantity
            TonKho: 0,
            ThanhTienNhap: 0,
            ThanhTienBan: 0,
            batches: new Map(),
          });
        }
        const product = productMap.get(key);
        product.SoLuongKiemKe += item.SoLuongKiemKe || 0;

        // Add or update batch details
        const batchKey = item.LoSX;
        if (!product.batches.has(batchKey)) {
          product.batches.set(batchKey, {
            LoSX: item.LoSX,
            NgaySX: item.NgaySX,
            NgaySD: item.NgaySD,
            DonGiaLoSX: item.DonGiaLoSX,
            SoLuongNhap: 0,
            SoLuongBan: 0,
            SoLuongDieuChuyen: 0,
            SoLuongPhanBo: 0,
            SoLuongTraVe: 0,
            SoLuongKiemKe: 0, // Add audit quantity
            TonKho: 0,
            ThanhTienNhap: 0,
            ThanhTienBan: 0,
          });
        }
        const batch = product.batches.get(batchKey);
        batch.SoLuongKiemKe += item.SoLuongKiemKe || 0;
      });
    }

    // Calculate inventory using API data for each product and batch
    const inventory = Array.from(productMap.values()).map((product) => {
      // Calculate inventory for each batch using API data
      const batchArray = Array.from(product.batches.values()).map((batch) => {
        // Get actual inventory from API
        const inventoryKey = `${product.MaSP}-${batch.LoSX}`;
        const actualTonKho = inventoryLookup.has(inventoryKey)
          ? inventoryLookup.get(inventoryKey)
          : batch.TonKho;
        const actualAuditQty = auditLookup.has(inventoryKey)
          ? auditLookup.get(inventoryKey)
          : batch.SoLuongKiemKe;

        return {
          ...batch,
          TonKho: actualTonKho,
          SoLuongKiemKe: actualAuditQty,
        };
      });

      // Calculate total inventory and audit from batches
      const totalTonKho = batchArray.reduce(
        (sum, batch) => sum + batch.TonKho,
        0
      );
      const totalSoLuongKiemKe = batchArray.reduce(
        (sum, batch) => sum + batch.SoLuongKiemKe,
        0
      );

      return {
        ...product,
        TonKho: totalTonKho,
        SoLuongKiemKe: totalSoLuongKiemKe,
        batches: batchArray,
      };
    });

    return inventory;
  };

  // Toggle product expansion (Level 1)
  const toggleProductExpansion = (maSP) => {
    const newExpandedProducts = new Set(expandedProducts);
    if (newExpandedProducts.has(maSP)) {
      newExpandedProducts.delete(maSP);
    } else {
      newExpandedProducts.add(maSP);
    }
    setExpandedProducts(newExpandedProducts);
  };

  // Toggle store expansion (Level 2)
  const toggleStoreExpansion = (maSP, storeId) => {
    const key = `${maSP}-${storeId}`;
    const newExpandedStores = new Set(expandedStores);
    if (newExpandedStores.has(key)) {
      newExpandedStores.delete(key);
    } else {
      newExpandedStores.add(key);
    }
    setExpandedStores(newExpandedStores);
  };

  // Toggle row expansion (for single store mode)
  const toggleRowExpansion = (maSP) => {
    const newExpandedRows = new Set(expandedBatches);
    if (newExpandedRows.has(maSP)) {
      newExpandedRows.delete(maSP);
    } else {
      newExpandedRows.add(maSP);
    }
    setExpandedBatches(newExpandedRows);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN");
  };

  // Check if batch is expiring soon (within 7 days)
  const isExpiringSoon = (ngaySD) => {
    if (!ngaySD) return false;
    const expiryDate = new Date(ngaySD);
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  // Check if batch is expired
  const isExpired = (ngaySD) => {
    if (!ngaySD) return false;
    const expiryDate = new Date(ngaySD);
    const today = new Date();
    return expiryDate < today;
  };
  const getDaysRemaining = (ngaySD) => {
    if (!ngaySD) return null;
    const expiryDate = new Date(ngaySD);
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Add function to check if product has non-expired batches
  const hasNonExpiredBatches = (item) => {
    if (isAllStoresMode && item.stores) {
      return item.stores.some(
        (store) =>
          store.batches &&
          store.batches.some((batch) => !isExpired(batch.NgaySD))
      );
    } else if (item.batches && item.batches.length > 0) {
      return item.batches.some((batch) => !isExpired(batch.NgaySD));
    }
    return true; // If no batches, consider as not expired
  };

  // Add new helper function to check if product has batches expiring within X days
  const hasBatchesExpiringWithin = (item, days) => {
    if (isAllStoresMode && item.stores) {
      return item.stores.some(
        (store) =>
          store.batches &&
          store.batches.some((batch) => {
            if (!batch.NgaySD) return false;
            const expiryDate = new Date(batch.NgaySD);
            const today = new Date();
            const diffTime = expiryDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays >= 0 && diffDays <= days; // Expiring within X days (including today)
          })
      );
    } else if (item.batches && item.batches.length > 0) {
      return item.batches.some((batch) => {
        if (!batch.NgaySD) return false;
        const expiryDate = new Date(batch.NgaySD);
        const today = new Date();
        const diffTime = expiryDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= days; // Expiring within X days (including today)
      });
    }
    return false;
  };

  // Update getFilteredInventoryData to filter products based on batch criteria
  const getFilteredInventoryData = () => {
    let filtered = inventoryData;

    // Filter by search term
    if (productSearchTerm.trim()) {
      const searchLower = normalizeText(productSearchTerm).trim();
      filtered = filtered.filter((item) => {
        const productCodeText = normalizeText(item?.MaSP);
        const productNameText = normalizeText(item?.Ten_SP);
        return (
          productCodeText.includes(searchLower) ||
          productNameText.includes(searchLower)
        );
      });
    }

    // Filter by stock status
    if (filterInStock) {
      filtered = filtered.filter((item) => {
        const stock = isAllStoresMode ? item.totalTonKho : item.TonKho;
        return stock > 0;
      });
    }

    // Filter by expiry status
    filtered = filtered.filter((item) => {
      const hasValidBatch = (batches) =>
        batches.some((batch) => {
          const days = getDaysRemaining(batch.NgaySD);
          if (filterNotExpired && days < 0) return false;
          if (
            filterExpiringDays !== null &&
            !(days >= 0 && days <= filterExpiringDays)
          )
            return false;
          return true;
        });

      if (isAllStoresMode && item.stores) {
        // Ensure only stores with valid batches are included
        item.stores = item.stores.filter((store) =>
          hasValidBatch(store.batches || [])
        );
        return item.stores.length > 0; // Only include products with matching stores
      } else if (item.batches && item.batches.length > 0) {
        return hasValidBatch(item.batches);
      }
      return false;
    });

    // Recalculate totals from filtered batches upwards
    filtered.forEach((item) => {
      if (isAllStoresMode && item.stores) {
        // CASE 1: All stores - Filter batches for each store, then recalculate store totals from filtered batches (Level 2 from Level 3)
        item.stores.forEach((store) => {
          if (store.batches && store.batches.length > 0) {
            // Filter batches based on expiry criteria
            store.batches = store.batches.filter((batch) => {
              const days = getDaysRemaining(batch.NgaySD);
              if (filterNotExpired && days < 0) return false;
              if (
                filterExpiringDays !== null &&
                !(days >= 0 && days <= filterExpiringDays)
              )
                return false;
              return true;
            });
            // Recalculate store totals from filtered batches
            store.SoLuongNhap = store.batches.reduce(
              (sum, batch) => sum + (batch.SoLuongNhap || 0),
              0
            );
            store.SoLuongBan = store.batches.reduce(
              (sum, batch) => sum + (batch.SoLuongBan || 0),
              0
            );
            store.SoLuongDieuChuyen = store.batches.reduce(
              (sum, batch) => sum + (batch.SoLuongDieuChuyen || 0),
              0
            );
            store.SoLuongPhanBo = store.batches.reduce(
              (sum, batch) => sum + (batch.SoLuongPhanBo || 0),
              0
            );
            store.SoLuongTraVe = store.batches.reduce(
              (sum, batch) => sum + (batch.SoLuongTraVe || 0),
              0
            );
            store.SoLuongKiemKe = store.batches.reduce(
              (sum, batch) => sum + (batch.SoLuongKiemKe || 0),
              0
            );
            store.TonKho = store.batches.reduce(
              (sum, batch) => sum + (batch.TonKho || 0),
              0
            );
            store.ThanhTienNhap = store.batches.reduce(
              (sum, batch) => sum + (batch.ThanhTienNhap || 0),
              0
            );
            store.ThanhTienBan = store.batches.reduce(
              (sum, batch) => sum + (batch.ThanhTienBan || 0),
              0
            );
          }
        });
        // CASE 1: Recalculate product totals from filtered stores (Level 1 from Level 2)
        item.totalSoLuongNhap = item.stores.reduce(
          (sum, store) => sum + store.SoLuongNhap,
          0
        );
        item.totalSoLuongBan = item.stores.reduce(
          (sum, store) => sum + store.SoLuongBan,
          0
        );
        item.totalSoLuongDieuChuyen = item.stores.reduce(
          (sum, store) => sum + store.SoLuongDieuChuyen,
          0
        );
        item.totalSoLuongPhanBo = item.stores.reduce(
          (sum, store) => sum + store.SoLuongPhanBo,
          0
        );
        item.totalSoLuongTraVe = item.stores.reduce(
          (sum, store) => sum + store.SoLuongTraVe,
          0
        );
        item.totalSoLuongKiemKe = item.stores.reduce(
          (sum, store) => sum + store.SoLuongKiemKe,
          0
        );
        item.totalTonKho = item.stores.reduce(
          (sum, store) => sum + store.TonKho,
          0
        );
        item.totalThanhTienNhap = item.stores.reduce(
          (sum, store) => sum + store.ThanhTienNhap,
          0
        );
        item.totalThanhTienBan = item.stores.reduce(
          (sum, store) => sum + store.ThanhTienBan,
          0
        );
        // Recalculate store count based on filtered stores with stock
        item.storeCount = item.stores.filter(
          (store) => store.TonKho > 0
        ).length;
      } else if (item.batches && item.batches.length > 0) {
        // CASE 2: Single store - Filter batches, then recalculate product totals from filtered batches (Level 1 from Level 2)
        item.batches = item.batches.filter((batch) => {
          const days = getDaysRemaining(batch.NgaySD);
          if (filterNotExpired && days < 0) return false;
          if (
            filterExpiringDays !== null &&
            !(days >= 0 && days <= filterExpiringDays)
          )
            return false;
          return true;
        });
        item.SoLuongNhap = item.batches.reduce(
          (sum, batch) => sum + (batch.SoLuongNhap || 0),
          0
        );
        item.SoLuongBan = item.batches.reduce(
          (sum, batch) => sum + (batch.SoLuongBan || 0),
          0
        );
        item.SoLuongDieuChuyen = item.batches.reduce(
          (sum, batch) => sum + (batch.SoLuongDieuChuyen || 0),
          0
        );
        item.SoLuongPhanBo = item.batches.reduce(
          (sum, batch) => sum + (batch.SoLuongPhanBo || 0),
          0
        );
        item.SoLuongTraVe = item.batches.reduce(
          (sum, batch) => sum + (batch.SoLuongTraVe || 0),
          0
        );
        item.SoLuongKiemKe = item.batches.reduce(
          (sum, batch) => sum + (batch.SoLuongKiemKe || 0),
          0
        );
        item.TonKho = item.batches.reduce(
          (sum, batch) => sum + (batch.TonKho || 0),
          0
        ); // Fix: Recalculate TonKho from batches
        item.ThanhTienNhap = item.batches.reduce(
          (sum, batch) => sum + (batch.ThanhTienNhap || 0),
          0
        );
        item.ThanhTienBan = item.batches.reduce(
          (sum, batch) => sum + (batch.ThanhTienBan || 0),
          0
        );
      }
    });

    return filtered;
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch sales, import, inventory, and audit data concurrently
      const [salesResult, importResult, inventoryResult, auditResult] =
        await Promise.all([
          fetchSalesData(),
          fetchImportData(),
          fetchInventoryData(),
          fetchAuditData(),
        ]);

      // Calculate total inventory for each store
      const inventoryTotals = new Map();
      if (inventoryResult.success && inventoryResult.sumaryByStore) {
        Object.entries(inventoryResult.sumaryByStore).forEach(([storeId, total]) => {
          inventoryTotals.set(storeId, total);
        });
      }

      // Update stores with total inventory
      setStores(prevStores =>
        prevStores.map(store => ({
          ...store,
          totalInventory: inventoryTotals.has(store.IDCH.toString())
            ? inventoryTotals.get(store.IDCH.toString())
            : store.totalInventory,
        }))
      );
      setFilteredStores(prevFiltered =>
        prevFiltered.map(store => ({
          ...store,
          totalInventory: store.IDCH === "ALL"
            ? 0
            : (inventoryTotals.has(store.IDCH.toString())
                ? inventoryTotals.get(store.IDCH.toString())
                : store.totalInventory),
        }))
      );

      if (
        salesResult.success &&
        importResult.success &&
        inventoryResult.success
      ) {
        if (isAllStoresMode) {
          // Handle multi-store mode - API returns dataByStore structure
          setMultiStoreData({
            sales: salesResult,
            import: importResult,
          });
          setSummary(salesResult.grandSummary || {});
          setImportSummary(importResult.grandSummary || {});

          // Group multi-store data with inventory and audit
          const inventory = groupMultiStoreData(
            salesResult,
            importResult,
            inventoryResult,
            auditResult
          );
          setInventoryData(inventory);
        } else {
          // Handle single store mode - Extract data from dataByStore structure
          const salesData =
            salesResult.dataByStore && salesResult.dataByStore[maCH]
              ? salesResult.dataByStore[maCH]
              : [];
          const importData =
            importResult.dataByStore && importResult.dataByStore[maCH]
              ? importResult.dataByStore[maCH]
              : [];

          setData(salesData);
          setImportData(importData);

          // Extract summary for the specific store
          const storeSummary =
            salesResult.summaryByStore && salesResult.summaryByStore[maCH]
              ? salesResult.summaryByStore[maCH]
              : {};
          const storeImportSummary =
            importResult.summaryByStore && importResult.summaryByStore[maCH]
              ? importResult.summaryByStore[maCH]
              : {};

          setSummary(storeSummary);
          setImportSummary(storeImportSummary);

          // Handle distribution data for store 20
          let distributionDataArray = [];
          if (maCH === "20" && importResult.xuatPhanBoKho) {
            distributionDataArray = importResult.xuatPhanBoKho.data || [];
            setDistributionData(distributionDataArray);
          }

          // Use existing grouping function for single store with inventory and audit data
          const inventory = groupProductsByMaSP(
            salesData,
            importData,
            distributionDataArray,
            inventoryResult,
            auditResult
          );
          setInventoryData(inventory);
        }
      } else {
        setError("Không thể tải dữ liệu");
      }
    } catch (err) {
      setError("Tải dữ liệu bị gián đoạn, vui lòng reload lại trang !");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (maCH && ngayBatDau && ngayKetThuc) {
      fetchData();
    }
  }, [maCH, ngayBatDau, ngayKetThuc]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat("vi-VN").format(number || 0);
  };

  const exportToExcel = () => {
    try {
      // Get filtered data for export
      const filteredData = getFilteredInventoryData();

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Create summary worksheet with filter information
      const summaryData = [
        ["THỐNG KÊ BÁNH TRUNG THU"],
        [""],
        ["Thông tin báo cáo:"],
        [
          `Cửa hàng: ${
            selectedStore
              ? `${selectedStore.IDCH} - ${selectedStore.TenCuaHang}`
              : maCH
          }`,
        ],
        [`Từ ngày: ${ngayBatDau} đến ngày: ${ngayKetThuc}`],
        [`Xuất báo cáo: ${new Date().toLocaleString("vi-VN")}`],
        [""],
        ["Bộ lọc áp dụng:"],
        [`Tìm kiếm: ${productSearchTerm || "Không"}`],
        [`Chỉ hiển thị có hàng: ${filterInStock ? "Có" : "Không"}`],
        [`Chỉ hiển thị chưa hết hạn: ${filterNotExpired ? "Có" : "Không"}`],
        [
          `Còn lại < ${filterExpiringDays || 0} ngày: ${
            filterExpiringDays !== null ? "Có" : "Không"
          }`,
        ], // New filter info
        [""],
        ["Tổng kết (sau lọc):"],
        [`Tổng sản phẩm: ${filteredData.length}`],
        [
          `Tổng số lượng bán: ${formatNumber(
            filteredData.reduce(
              (sum, item) =>
                sum +
                (isAllStoresMode ? item.totalSoLuongBan : item.SoLuongBan),
              0
            )
          )}`,
        ],
        [
          `Tổng số lượng nhập: ${formatNumber(
            filteredData.reduce(
              (sum, item) =>
                sum +
                (isAllStoresMode ? item.totalSoLuongNhap : item.SoLuongNhap),
              0
            )
          )}`,
        ],
        [
          `Tồn kho: ${formatNumber(
            filteredData.reduce(
              (sum, item) =>
                sum + (isAllStoresMode ? item.totalTonKho : item.TonKho),
              0
            )
          )}`,
        ],
      ];

      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Create data worksheet with conditional column header using filtered data
      const excelData = filteredData.map((item, index) => ({
        STT: index + 1,
        "Mã SP": item.MaSP,
        "Tên Sản Phẩm": item.Ten_SP,
        ĐVT: item.DVT_SP,
        "SL Nhập": isAllStoresMode ? item.totalSoLuongNhap : item.SoLuongNhap,
        "SL Bán": isAllStoresMode ? item.totalSoLuongBan : item.SoLuongBan,
        [isAllStoresMode
          ? "SL Điều Chuyển"
          : maCH === "20"
          ? "SL Phân Bổ"
          : "SL Điều Chuyển"]: isAllStoresMode
          ? item.totalSoLuongDieuChuyen
          : maCH === "20"
          ? item.SoLuongPhanBo
          : item.SoLuongDieuChuyen,
        "SL Trả Về": isAllStoresMode
          ? item.totalSoLuongTraVe
          : item.SoLuongTraVe,
        "Số Kiểm Kê Chênh Lệch": isAllStoresMode
          ? item.totalSoLuongKiemKe
          : item.SoLuongKiemKe, // Add audit column
        "Tồn Kho": isAllStoresMode ? item.totalTonKho : item.TonKho,
        "Thành Tiền Nhập": isAllStoresMode
          ? item.totalThanhTienNhap
          : item.ThanhTienNhap,
        "Thành Tiền Bán": isAllStoresMode
          ? item.totalThanhTienBan
          : item.ThanhTienBan,
        ...(isAllStoresMode && {
          "Số CH có hàng": `${item.storeCount}/${getBakeryStoreIds().length}`,
        }),
      }));

      const dataWorksheet = XLSX.utils.json_to_sheet(excelData);

      // Style the inventory column (Tồn Kho) with red color
      const range = XLSX.utils.decode_range(dataWorksheet["!ref"]);
      const inventoryColIndex = isAllStoresMode ? 9 : 9; // Tồn Kho column position (adjusted for new column)

      // Apply red background to inventory column
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({
          r: R,
          c: inventoryColIndex,
        });
        if (!dataWorksheet[cellAddress]) continue;

        if (!dataWorksheet[cellAddress].s) dataWorksheet[cellAddress].s = {};
        dataWorksheet[cellAddress].s.fill = {
          fgColor: { rgb: "FFCCCC" }, // Light red background
          bgColor: { rgb: "FFCCCC" },
        };
        dataWorksheet[cellAddress].s.font = {
          color: { rgb: "CC0000" }, // Dark red text
          bold: true,
        };
      }

      // Add detailed batch data worksheet with product separators
      if (filteredData.length > 0) {
        const detailData = [];
        let currentRowIndex = 0;

        filteredData.forEach((item, itemIndex) => {
          // Add product header row
          detailData.push({
            STT: `SẢN PHẨM ${itemIndex + 1}`,
            "Mã SP": item.MaSP,
            "Tên SP": item.Ten_SP,
            "Cửa Hàng": "",
            "MaBravo": "",
            "Lô SX": "",
            "Ngày SX": "",
            "Hạn SD": "",
            "Đơn Giá": "",
            "SL Nhập": "",
            "SL Bán": "",
            "SL Điều Chuyển": "",
            "SL Trả Về": "",
            "Số Kiểm Kê Chênh Lệch": isAllStoresMode
              ? item.totalSoLuongKiemKe
              : item.SoLuongKiemKe, // Add audit column
            "Tồn Kho": isAllStoresMode ? item.totalTonKho : item.TonKho,
            "TT Nhập": "",
            "TT Bán": "",
            "Trạng Thái": "TỔNG SẢN PHẨM",
          });
          currentRowIndex++;

          if (isAllStoresMode && item.stores) {
            // Multi-store mode: include store and batch details
            item.stores.forEach((store, storeIndex) => {
              if (store.batches && store.batches.length > 0) {
                store.batches.forEach((batch, batchIndex) => {
                  detailData.push({
                    STT: `${itemIndex + 1}.${storeIndex + 1}.${batchIndex + 1}`,
                    "Mã SP": item.MaSP,
                    "Tên SP": item.Ten_SP,
                    "Cửa Hàng": `${store.storeId} - ${store.storeName}`,
                    "MaBravo": store.maBravo || "",
                    "Lô SX": batch.LoSX,
                    "Ngày SX": formatDate(batch.NgaySX),
                    "Hạn SD": formatDate(batch.NgaySD),
                    "Đơn Giá": batch.DonGiaLoSX,
                    "SL Nhập": batch.SoLuongNhap,
                    "SL Bán": batch.SoLuongBan,
                    "SL Điều Chuyển":
                      store.storeId === "20"
                        ? batch.SoLuongPhanBo
                        : batch.SoLuongDieuChuyen,
                    "SL Trả Về": batch.SoLuongTraVe,
                    "Số Kiểm Kê Chênh Lệch": batch.SoLuongKiemKe, // Add audit column
                    "Tồn Kho": batch.TonKho,
                    "TT Nhập": batch.ThanhTienNhap,
                    "TT Bán": batch.ThanhTienBan,
                    "Trạng Thái": isExpired(batch.NgaySD)
                      ? "Hết hạn"
                      : isExpiringSoon(batch.NgaySD)
                      ? "Sắp hết hạn"
                      : "Còn hạn",
                  });
                  currentRowIndex++;
                });
              }
            });
          } else if (item.batches && item.batches.length > 0) {
            // Single store mode: include batch details
            item.batches.forEach((batch, batchIndex) => {
              detailData.push({
                STT: `${itemIndex + 1}.${batchIndex + 1}`,
                "Mã SP": item.MaSP,
                "Tên SP": item.Ten_SP,
                "Lô SX": batch.LoSX,
                "Ngày SX": formatDate(batch.NgaySX),
                "Hạn SD": formatDate(batch.NgaySD),
                "Đơn Giá": batch.DonGiaLoSX,
                "SL Nhập": batch.SoLuongNhap,
                "SL Bán": batch.SoLuongBan,
                [maCH === "20" ? "SL Phân Bổ" : "SL Điều Chuyển"]:
                  maCH === "20" ? batch.SoLuongPhanBo : batch.SoLuongDieuChuyen,
                "SL Trả Về": batch.SoLuongTraVe,
                "Số Kiểm Kê Chênh Lệch": batch.SoLuongKiemKe, // Add audit column
                "Tồn Kho": batch.TonKho,
                "TT Nhập": batch.ThanhTienNhap,
                "TT Bán": batch.ThanhTienBan,
                "Trạng Thái": isExpired(batch.NgaySD)
                  ? "Hết hạn"
                  : isExpiringSoon(batch.NgaySD)
                  ? "Sắp hết hạn"
                  : "Còn hạn",
              });
              currentRowIndex++;
            });
          }

          // Add separator row between products (except for last product)
          if (itemIndex < filteredData.length - 1) {
            detailData.push({
              STT: "---",
              "Mã SP": "---",
              "Tên SP": "---",
              "Cửa Hàng": "---",
              "MaBravo": "---",
              "Lô SX": "---",
              "Ngày SX": "---",
              "Hạn SD": "---",
              "Đơn Giá": "---",
              "SL Nhập": "---",
              "SL Bán": "---",
              "SL Điều Chuyển": "---",
              "SL Trả Về": "---",
              "Số Kiểm Kê Chênh Lệch": "---",
              "Tồn Kho": "---",
              "TT Nhập": "---",
              "TT Bán": "---",
              "Trạng Thái": "---",
            });
            currentRowIndex++;
          }
        });

        if (detailData.length > 0) {
          const detailWorksheet = XLSX.utils.json_to_sheet(detailData);

          // Style the detail worksheet
          const detailRange = XLSX.utils.decode_range(detailWorksheet["!ref"]);
          const detailInventoryColIndex = isAllStoresMode ? 12 : 11; // Tồn Kho column in detail sheet

          let productHeaderRows = [];
          let separatorRows = [];

          // Identify product header rows and separator rows
          for (let R = detailRange.s.r + 1; R <= detailRange.e.r; ++R) {
            const sttCell =
              detailWorksheet[XLSX.utils.encode_cell({ r: R, c: 0 })];
            if (
              sttCell &&
              sttCell.v &&
              sttCell.v.toString().includes("SẢN PHẨM")
            ) {
              productHeaderRows.push(R);
            } else if (sttCell && sttCell.v === "---") {
              separatorRows.push(R);
            }
          }

          // Style inventory column with red color
          for (let R = detailRange.s.r + 1; R <= detailRange.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({
              r: R,
              c: detailInventoryColIndex,
            });
            if (!detailWorksheet[cellAddress]) continue;

            if (!detailWorksheet[cellAddress].s)
              detailWorksheet[cellAddress].s = {};
            detailWorksheet[cellAddress].s.fill = {
              fgColor: { rgb: "FFCCCC" },
              bgColor: { rgb: "FFCCCC" },
            };
            detailWorksheet[cellAddress].s.font = {
              color: { rgb: "CC0000" },
              bold: true,
            };
          }

          // Style product header rows with blue background
          productHeaderRows.forEach((rowIndex) => {
            for (let C = detailRange.s.c; C <= detailRange.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: C });
              if (!detailWorksheet[cellAddress]) continue;

              if (!detailWorksheet[cellAddress].s)
                detailWorksheet[cellAddress].s = {};
              detailWorksheet[cellAddress].s.fill = {
                fgColor: { rgb: "CCE5FF" },
                bgColor: { rgb: "CCE5FF" },
              };
              detailWorksheet[cellAddress].s.font = {
                color: { rgb: "000080" },
                bold: true,
              };
            }
          });

          // Style separator rows with gray background
          separatorRows.forEach((rowIndex) => {
            for (let C = detailRange.s.c; C <= detailRange.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: C });
              if (!detailWorksheet[cellAddress]) continue;

              if (!detailWorksheet[cellAddress].s)
                detailWorksheet[cellAddress].s = {};
              detailWorksheet[cellAddress].s.fill = {
                fgColor: { rgb: "F0F0F0" },
                bgColor: { rgb: "F0F0F0" },
              };
            }
          });

          // Set column widths for detail worksheet
          const detailColumnWidths = [
            { wch: 10 }, // STT
            { wch: 12 }, // Mã SP
            { wch: 30 }, // Tên SP
            ...(isAllStoresMode ? [{ wch: 25 }] : []), // Cửa Hàng
            ...(isAllStoresMode ? [{ wch: 12 }] : []), // MaBravo
            { wch: 10 }, // Lô SX
            { wch: 12 }, // Ngày SX
            { wch: 12 }, // Hạn SD
            { wch: 12 }, // Đơn Giá
            { wch: 10 }, // SL Nhập
            { wch: 10 }, // SL Bán
            { wch: 12 }, // SL Điều Chuyển/Phân Bổ
            { wch: 10 }, // SL Trả Về
            { wch: 10 }, // Số Kiểm Kê (new column)
            { wch: 12 }, // Tồn Kho (wider for red color visibility)
            { wch: 15 }, // TT Nhập
            { wch: 15 }, // TT Bán
            { wch: 12 }, // Trạng Thái
          ];
          detailWorksheet["!cols"] = detailColumnWidths;

          XLSX.utils.book_append_sheet(
            workbook,
            detailWorksheet,
            "Chi Tiết Theo Lô"
          );
        }
      }

      // Set column widths for data worksheet
      const columnWidths = [
        { wch: 5 }, // STT
        { wch: 12 }, // Mã SP
        { wch: 40 }, // Tên Sản Phẩm
        { wch: 8 }, // ĐVT
        { wch: 10 }, // SL Nhập
        { wch: 10 }, // SL Bán
        { wch: 12 }, // SL Điều Chuyển
        { wch: 10 }, // SL Trả Về
        { wch: 10 }, // Số Kiểm Kê (new column)
        { wch: 12 }, // Tồn Kho (wider for red color visibility)
        ...(isAllStoresMode ? [{ wch: 12 }] : []), // Số CH có hàng
        { wch: 15 }, // Thành Tiền Nhập
        { wch: 15 }, // Thành Tiền Bán
      ];
      dataWorksheet["!cols"] = columnWidths;
      summaryWorksheet["!cols"] = [{ wch: 50 }];

      // Add worksheets to workbook
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Tổng Kết");
      XLSX.utils.book_append_sheet(workbook, dataWorksheet, "Dữ Liệu Chính");

      // Generate filename with date range and filter info
      let filterSuffix = "";
      if (productSearchTerm || filterInStock || filterNotExpired) {
        filterSuffix = "_Filtered";
      }

      const fileName = `ThongKe_BanhTrungThu_CH${maCH}_${ngayBatDau.replace(
        /-/g,
        ""
      )}_${ngayKetThuc.replace(/-/g, "")}${filterSuffix}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Có lỗi xảy ra khi xuất file Excel. Vui lòng thử lại.");
    }
  };

  // Add function to export sales receipts to Excel
  const exportSalesReceiptsToExcel = async (allStores = false) => {
    try {
      const storeIds = allStores ? getBakeryStoreIds().join(",") : maCH;

      // Show loading state
      setLoading(true);

      // Fetch data
      const salesReceiptsData = await fetchSalesReceiptsData(storeIds);

      if (salesReceiptsData.success && salesReceiptsData.data) {
        // Process data for Excel export        const excelData = [];
        const excelData = [];
        // Flatten the data structure
        salesReceiptsData.data.forEach((receipt) => {
          receipt.ChiTiet.forEach((item) => {
            excelData.push({
              ID: receipt.IDXuat,
              MaBravo: receipt.MaBravo,
              "Ngày Bán": formatDate(receipt.NgayXuat),
              "Mã Sản Phẩm": item.MaSP,
              "Tên Sản Phẩm": item.TenSP,
              "Số Lượng": item.SoLuong,
              "Đơn Giá": item.GiaBan,
              "Thành Tiền": item.ThanhTien,
              "Mã Cửa Hàng": receipt.MaCH,
              "Người Bán": receipt.TenUser,
              "Lô SX": item.LoSX,
            });
          });
        });

        // Create workbook
        const workbook = XLSX.utils.book_new();

        // Add summary sheet
        const summaryData = [
          ["BÁO CÁO CHI TIẾT HÓA ĐƠN BÁN HÀNG BÁNH TRUNG THU"],
          [""],
          ["Thông tin báo cáo:"],
          [
            `Cửa hàng: ${
              allStores
                ? "Tất cả cửa hàng Bakery"
                : `${
                    selectedStore
                      ? `${selectedStore.IDCH} - ${selectedStore.TenCuaHang}`
                      : maCH
                  }`
            }`,
          ],
          [`Từ ngày: ${ngayBatDau} đến ngày: ${ngayKetThuc}`],
          [`Xuất báo cáo: ${new Date().toLocaleString("vi-VN")}`],
          [""],
          [`Tổng số hóa đơn: ${salesReceiptsData.data.length}`],
          [
            `Tổng doanh thu: ${formatNumber(
              salesReceiptsData.data.reduce(
                (sum, receipt) => sum + receipt.TongThanhTien,
                0
              )
            )} VNĐ`,
          ],
          [
            `Tổng số lượng sản phẩm: ${formatNumber(
              salesReceiptsData.data.reduce(
                (sum, receipt) => sum + receipt.TongSoLuong,
                0
              )
            )}`,
          ],
        ];

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Tổng Kết");

        // Add data sheet
        const dataSheet = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        const columnWidths = [
          { wch: 10 }, // ID
          { wch: 8 }, // MaBravo
          { wch: 12 }, // Ngày Bán
          { wch: 12 }, // Mã Sản Phẩm
          { wch: 40 }, // Tên Sản Phẩm
          { wch: 10 }, // Số Lượng
          { wch: 12 }, // Đơn Giá
          { wch: 15 }, // Thành Tiền
          { wch: 10 }, // Mã Cửa Hàng
          { wch: 15 }, // Người Bán
          { wch: 10 }, // Lô SX
        ];
        dataSheet["!cols"] = columnWidths;

        XLSX.utils.book_append_sheet(workbook, dataSheet, "Chi Tiết Hóa Đơn");

        // Generate filename
        const storeSuffix = allStores ? "TatCaCuaHang" : `CH${maCH}`;
        const fileName = `HoaDonBanHang_BanhTrungThu_${storeSuffix}_${ngayBatDau.replace(
          /-/g,
          ""
        )}_${ngayKetThuc.replace(/-/g, "")}.xlsx`;

        // Save file
        XLSX.writeFile(workbook, fileName);
      } else {
        alert("Không có dữ liệu hóa đơn bán hàng trong khoảng thời gian này.");
      }
    } catch (error) {
      console.error("Error exporting sales receipts:", error);
      alert("Có lỗi xảy ra khi xuất dữ liệu hóa đơn. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <NavbarTrungThu />
    <div className="ds-trung-thu-container">
      <div className="container-fluid">
        {/* Simple Header */}
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-chart-bar me-3"></i>
            Thống Kê Bán Bánh Trung Thu
          </h1>
          <p className="page-subtitle">Báo cáo doanh số theo ngày</p>
        </div>

        {/* Enhanced Filter Form with Store Search */}
        <div className="card filter-section">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>
              Bộ Lọc Thống Kê
            </h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSearch} className="row g-3">
              <div className="col-md-3">
                <label htmlFor="storeSearch" className="form-label">
                  Tìm kiếm cửa hàng
                </label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    id="storeSearch"
                    value={storeSearchTerm}
                    onChange={(e) => handleStoreSearch(e.target.value)}
                    onFocus={() => setShowStoreDropdown(true)}
                    placeholder="Nhập mã hoặc tên cửa hàng..."
                    autoComplete="off"
                  />
                  <span
                    className="input-group-text position-absolute top-50 end-0 translate-middle-y"
                    style={{
                      border: "none",
                      background: "transparent",
                      zIndex: 10,
                    }}
                  >
                    <i className="fas fa-search"></i>
                  </span>

                  {showStoreDropdown && filteredStores.length > 0 && (
                    <div
                      className="dropdown-menu show w-100"
                      style={{
                        maxHeight: "400px",
                        overflowY: "auto",
                        whiteSpace: "nowrap", // Prevent text wrapping to allow dropdown to expand width if needed
                        minWidth: "100%", // Ensure minimum width matches input
                      }}
                    >
                      {filteredStores.map((store) => (
                        <button
                          key={store.IDCH}
                          type="button"
                          className="dropdown-item"
                          onClick={() => handleStoreSelect(store)}
                        >
                          <strong>
                            {store.IDCH === "ALL" ? "TẤT CẢ" : store.IDCH}
                          </strong>{" "}
                          - {store.TenCuaHang}
                          {store.IDCH !== "ALL" && (
                            <span className="text-muted ms-2 float-end">
                              (Tồn: {formatNumber(store.totalInventory)})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <label htmlFor="ngayBatDau" className="form-label">
                  Từ Ngày
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="ngayBatDau"
                  value={ngayBatDau}
                  onChange={(e) => setNgayBatDau(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-3">
                <label htmlFor="ngayKetThuc" className="form-label">
                  Đến Ngày
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="ngayKetThuc"
                  value={ngayKetThuc}
                  onChange={(e) => setNgayKetThuc(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-2 d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Đang tải...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>
                      Tìm kiếm
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Close dropdown when clicking outside */}
        {showStoreDropdown && (
          <div
            className="position-fixed w-100 h-100"
            style={{ top: 0, left: 0, zIndex: 1 }}
            onClick={() => setShowStoreDropdown(false)}
          ></div>
        )}

        {/* Enhanced Summary Cards */}
        {summary && Object.keys(summary).length > 0 && (
          <div className="summary-section">
            <div className="row g-3">
              <div className="col-3">
                <div className="summary-card primary card-hover">
                  <i className="fas fa-box summary-icon"></i>
                  <div className="summary-number">
                    {getFilteredInventoryData().length}
                  </div>
                  <div className="summary-label">Tổng Sản Phẩm</div>
                </div>
              </div>
              <div className="col-3">
                <div className="summary-card success card-hover">
                  <i className="fas fa-chart-line summary-icon"></i>
                  <div className="summary-number">
                    {formatNumber(
                      getFilteredInventoryData().reduce(
                        (sum, item) =>
                          sum +
                          (isAllStoresMode
                            ? item.totalSoLuongBan
                            : item.SoLuongBan),
                        0
                      )
                    )}
                  </div>
                  <div className="summary-label">Số Lượng Bán</div>
                </div>
              </div>
              <div className="col-3">
                <div className="summary-card info card-hover">
                  <i className="fas fa-truck summary-icon"></i>
                  <div className="summary-number">
                    {formatNumber(
                      getFilteredInventoryData().reduce(
                        (sum, item) =>
                          sum +
                          (isAllStoresMode
                            ? item.totalSoLuongNhap
                            : item.SoLuongNhap),
                        0
                      )
                    )}
                  </div>
                  <div className="summary-label">Số Lượng Nhập</div>
                </div>
              </div>

              <div className="col-3">
                <div className="summary-card warning card-hover">
                  <i className="fas fa-warehouse summary-icon"></i>
                  <div className="summary-number">
                    {formatNumber(
                      getFilteredInventoryData().reduce(
                        (sum, item) =>
                          sum +
                          (isAllStoresMode ? item.totalTonKho : item.TonKho),
                        0
                      )
                    )}
                  </div>
                  <div className="summary-label">Tồn Kho</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Enhanced Data Table with Multi-level Hierarchy */}
        <div className="table-section">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="mb-0">
              <i className="fas fa-table me-2"></i>
              Chi Tiết Thống Kê Bánh Trung Thu
              {isAllStoresMode && (
                <span className="badge bg-info ms-2">
                  Tất cả cửa hàng Bakery
                </span>
              )}
            </h5>

            {/* Search and Filter Controls */}
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {/* Product Search */}
              <div className="position-relative" style={{ minWidth: "250px" }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Tìm mã SP hoặc tên sản phẩm..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                />
                <i className="fas fa-search position-absolute top-50 end-0 translate-middle-y me-2 text-muted"></i>
              </div>

              {/* Filter Checkboxes */}
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="filterInStock"
                    checked={filterInStock}
                    onChange={(e) => setFilterInStock(e.target.checked)}
                  />
                  <label
                    className="form-check-label text-nowrap"
                    htmlFor="filterInStock"
                  >
                    <i className="fas fa-box-open me-1 text-success"></i>
                    Còn hàng
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="filterNotExpired"
                    checked={filterNotExpired}
                    onChange={(e) => setFilterNotExpired(e.target.checked)}
                  />
                  <label
                    className="form-check-label text-nowrap"
                    htmlFor="filterNotExpired"
                  >
                    <i className="fas fa-calendar-check me-1 text-primary"></i>
                    Chưa hết hạn
                  </label>
                </div>

                {/* Replace the two expiring checkboxes with this new input and button */}
                <div className="d-flex align-items-center gap-2">
                  <label
                    htmlFor="expiringDaysInput"
                    className="form-label mb-0 text-nowrap"
                  >
                    <i className="fas fa-clock me-1 text-warning"></i>
                    Số ngày còn hạn:
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    id="expiringDaysInput"
                    value={expiringDaysInput}
                    onChange={(e) => setExpiringDaysInput(e.target.value)}
                    placeholder="Nhập số ngày"
                    min="0"
                    style={{ width: "120px" }}
                  />
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => {
                      const days = parseInt(expiringDaysInput);
                      if (!isNaN(days) && days >= 0) {
                        setFilterExpiringDays(days);
                      } else {
                        setFilterExpiringDays(null);
                        setExpiringDaysInput("");
                      }
                    }}
                  >
                    Áp dụng
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setFilterExpiringDays(null);
                      setExpiringDaysInput("");
                    }}
                  >
                    Đặt lại
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                <p className="mt-3 text-muted">Đang tải dữ liệu...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th scope="col" width="40"></th>
                      <th scope="col">Mã SP</th>
                      <th scope="col">Tên Sản Phẩm</th>
                      <th scope="col">ĐVT</th>
                      <th scope="col" className="text-end">
                        SL Nhập
                      </th>
                      <th scope="col" className="text-end">
                        SL Bán
                      </th>
                      <th scope="col" className="text-end">
                        {isAllStoresMode
                          ? "SL Điều Chuyển"
                          : maCH === "20"
                          ? "SL Phân Bổ"
                          : "SL Điều Chuyển"}
                      </th>
                      <th scope="col" className="text-end">
                        SL Trả Về
                      </th>
                      <th scope="col" className="text-end">
                        K.Kê Chênh Lệch
                      </th>
                      <th scope="col" className="text-end">
                        Tồn Kho
                      </th>
                      {isAllStoresMode && (
                        <th scope="col" className="text-center">
                          Số CH có hàng
                        </th>
                      )}
                      <th scope="col" className="text-end">
                        TT Nhập
                      </th>
                      <th scope="col" className="text-end">
                        TT Bán
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredInventoryData().length > 0 ? (
                      getFilteredInventoryData().map((item, index) => (
                        <React.Fragment key={item.MaSP}>
                          {/* Level 1: Product Row */}
                          <tr className="table-row-hover">
                            <td>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  isAllStoresMode
                                    ? toggleProductExpansion(item.MaSP)
                                    : toggleRowExpansion(item.MaSP)
                                }
                                title={
                                  isAllStoresMode
                                    ? "Xem chi tiết theo cửa hàng"
                                    : "Xem chi tiết lô"
                                }
                              >
                                <i
                                  className={`fas fa-chevron-${
                                    isAllStoresMode
                                      ? expandedProducts.has(item.MaSP)
                                        ? "down"
                                        : "right"
                                      : expandedBatches.has(item.MaSP)
                                      ? "down"
                                      : "right"
                                  }`}
                                ></i>
                              </button>
                            </td>
                            <td>
                              <span className="badge bg-primary">
                                {item.MaSP}
                              </span>
                            </td>
                            <td className="fw-medium">{item.Ten_SP}</td>
                            <td>
                              <span className="badge bg-secondary">
                                {item.DVT_SP}
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold text-info">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalSoLuongNhap
                                    : item.SoLuongNhap
                                )}
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold text-success">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalSoLuongBan
                                    : item.SoLuongBan
                                )}
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold text-warning">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalSoLuongDieuChuyen
                                    : maCH === "20"
                                    ? item.SoLuongPhanBo
                                    : item.SoLuongDieuChuyen
                                )}
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold text-secondary">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalSoLuongTraVe
                                    : item.SoLuongTraVe
                                )}
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold text-info">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalSoLuongKiemKe
                                    : item.SoLuongKiemKe
                                )}
                              </span>
                            </td>
                            <td className="text-end">
                              <span
                                className={`fw-bold ${
                                  (isAllStoresMode
                                    ? item.totalTonKho
                                    : item.TonKho) < 0
                                    ? "text-danger"
                                    : (isAllStoresMode
                                        ? item.totalTonKho
                                        : item.TonKho) > 0
                                    ? "text-primary"
                                    : "text-muted"
                                }`}
                              >
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalTonKho
                                    : item.TonKho
                                )}
                              </span>
                            </td>
                            {isAllStoresMode && (
                              <td className="text-center">
                                <span className="badge bg-info">
                                  {item.storeCount}/{getBakeryStoreIds().length}
                                </span>
                              </td>
                            )}
                            <td className="text-end">
                              <span className="fw-semibold text-info">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalThanhTienNhap
                                    : item.ThanhTienNhap
                                )}{" "}
                                ₫
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold text-success">
                                {formatNumber(
                                  isAllStoresMode
                                    ? item.totalThanhTienBan
                                    : item.ThanhTienBan
                                )}{" "}
                                ₫
                              </span>
                            </td>
                          </tr>

                          {/* Level 2: Store Details (Multi-store mode) */}
                          {isAllStoresMode &&
                            expandedProducts.has(item.MaSP) && (
                              <tr>
                                <td
                                  colSpan={isAllStoresMode ? "13" : "12"}
                                  className="p-0"
                                >
                                  <div className="batch-details bg-light p-3">
                                    <h6 className="text-primary mb-3">
                                      <i className="fas fa-store me-2"></i>
                                      Chi Tiết Theo Cửa Hàng
                                    </h6>
                                    <div className="table-responsive">
                                      <table className="table table-sm table-striped mb-0">
                                        <thead>
                                          <tr>
                                            <th width="30"></th>
                                            <th>Cửa Hàng</th>
                                            <th className="text-end">
                                              SL Nhập
                                            </th>
                                            <th className="text-end">SL Bán</th>
                                            <th className="text-end">
                                              SL Điều Chuyển
                                            </th>
                                            <th className="text-end">
                                              SL Trả Về
                                            </th>
                                            <th className="text-end">
                                              K.Kê Chênh Lệch
                                            </th>
                                            <th className="text-end">
                                              Tồn Kho
                                            </th>
                                            <th className="text-end">
                                              TT Nhập
                                            </th>
                                            <th className="text-end">TT Bán</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {item.stores &&
                                            item.stores.map((store, sIndex) => (
                                              <React.Fragment key={sIndex}>
                                                <tr>
                                                  <td>
                                                    <button
                                                      className="btn btn-sm btn-outline-info"
                                                      onClick={() =>
                                                        toggleStoreExpansion(
                                                          item.MaSP,
                                                          store.storeId
                                                        )
                                                      }
                                                      title="Xem chi tiết lô"
                                                    >
                                                      <i
                                                        className={`fas fa-chevron-${
                                                          expandedStores.has(
                                                            `${item.MaSP}-${store.storeId}`
                                                          )
                                                            ? "down"
                                                            : "right"
                                                        }`}
                                                      ></i>
                                                    </button>
                                                  </td>
                                                  <td>
                                                    <strong>
                                                      {store.storeId}
                                                    </strong>{" "}
                                                    - {store.storeName}
                                                  </td>
                                                  <td className="text-end fw-semibold text-info">
                                                    {formatNumber(
                                                      store.SoLuongNhap
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-success">
                                                    {formatNumber(
                                                      store.SoLuongBan
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-warning">
                                                    {formatNumber(
                                                      store.storeId === "20"
                                                        ? store.SoLuongPhanBo
                                                        : store.SoLuongDieuChuyen
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-secondary">
                                                    {formatNumber(
                                                      store.SoLuongTraVe
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-info">
                                                    {formatNumber(
                                                      store.SoLuongKiemKe
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    <span
                                                      className={`fw-bold ${
                                                        store.TonKho < 0
                                                          ? "text-danger"
                                                          : store.TonKho > 0
                                                          ? "text-primary"
                                                          : "text-muted"
                                                      }`}
                                                    >
                                                      {formatNumber(
                                                        store.TonKho
                                                      )}
                                                    </span>
                                                  </td>
                                                  <td className="text-end fw-semibold text-info">
                                                    {formatNumber(
                                                      store.ThanhTienNhap
                                                    )}{" "}
                                                    ₫
                                                  </td>
                                                  <td className="text-end fw-semibold text-success">
                                                    {formatNumber(
                                                      store.ThanhTienBan
                                                    )}{" "}
                                                    ₫
                                                  </td>
                                                </tr>

                                                {/* Level 3: Batch Details */}
                                                {expandedStores.has(
                                                  `${item.MaSP}-${store.storeId}`
                                                ) && (
                                                  <tr>
                                                    <td
                                                      colSpan="10"
                                                      className="p-0"
                                                    >
                                                      <div
                                                        className="bg-white p-3"
                                                        style={{
                                                          marginLeft: "20px",
                                                        }}
                                                      >
                                                        <h6 className="text-secondary mb-3">
                                                          <i className="fas fa-list me-2"></i>
                                                          Chi Tiết Lô Sản Xuất
                                                        </h6>
                                                        {store.batches &&
                                                        store.batches.length >
                                                          0 ? (
                                                          <div className="table-responsive">
                                                            <table className="table table-sm mb-0">
                                                              <thead>
                                                                <tr>
                                                                  <th>Lô SX</th>
                                                                  <th>
                                                                    Ngày SX
                                                                  </th>
                                                                  <th>
                                                                    Hạn SD
                                                                  </th>
                                                                   <th>
                                                                    Hạn CL
                                                                  </th>
                                                                  <th className="text-end">
                                                                    Đơn Giá
                                                                  </th>
                                                                  <th className="text-end">
                                                                    SL Nhập
                                                                  </th>
                                                                  <th className="text-end">
                                                                    SL Bán
                                                                  </th>
                                                                  <th className="text-end">
                                                                    SL Điều
                                                                    Chuyển
                                                                  </th>
                                                                  <th className="text-end">
                                                                    SL Trả Về
                                                                  </th>
                                                                  <th className="text-end">
                                                                    Tồn Kho
                                                                  </th>
                                                                </tr>
                                                              </thead>
                                                              <tbody>
                                                                {store.batches
                                                                  .filter(
                                                                    (batch) => {
                                                                      const days =
                                                                        getDaysRemaining(
                                                                          batch.NgaySD
                                                                        );
                                                                      if (
                                                                        filterNotExpired &&
                                                                        days < 0
                                                                      )
                                                                        return false;
                                                                      if (
                                                                        filterExpiringDays !==
                                                                          null &&
                                                                        !(
                                                                          days >=
                                                                            0 &&
                                                                          days <=
                                                                            filterExpiringDays
                                                                        )
                                                                      )
                                                                        return false;
                                                                      return true;
                                                                    }
                                                                  )
                                                                  .map(
                                                                    (
                                                                      batch,
                                                                      bIndex
                                                                    ) => (
                                                                      <tr
                                                                        key={
                                                                          bIndex
                                                                        }
                                                                      >
                                                                        <td>
                                                                          <span className="badge bg-info">
                                                                            {
                                                                              batch.LoSX
                                                                            }
                                                                          </span>
                                                                        </td>
                                                                        <td>
                                                                          {formatDate(
                                                                            batch.NgaySX
                                                                          )}
                                                                        </td>
                                                                        <td>
                                                                          <span
                                                                            className={`${
                                                                              isExpired(
                                                                                batch.NgaySD
                                                                              )
                                                                                ? "text-danger fw-bold"
                                                                                : isExpiringSoon(
                                                                                    batch.NgaySD
                                                                                  )
                                                                                ? "text-warning fw-bold"
                                                                                : ""
                                                                            }`}
                                                                          >
                                                                            {formatDate(
                                                                              batch.NgaySD
                                                                            )}
                                                                            {isExpired(
                                                                              batch.NgaySD
                                                                            ) &&
                                                                              " (Hết hạn)"}
                                                                            {!isExpired(
                                                                              batch.NgaySD
                                                                            ) &&
                                                                              isExpiringSoon(
                                                                                batch.NgaySD
                                                                              ) &&
                                                                              " (Sắp hết hạn)"}
                                                                          </span>
                                                                        </td>
                                                                        <td className="text-start">
                                                                          {getDaysRemaining(
                                                                            batch.NgaySD
                                                                          ) !==
                                                                          null ? (
                                                                            <span
                                                                              className={`fw-bold ${
                                                                                getDaysRemaining(
                                                                                  batch.NgaySD
                                                                                ) <
                                                                                0
                                                                                  ? "text-danger"
                                                                                  : getDaysRemaining(
                                                                                      batch.NgaySD
                                                                                    ) <=
                                                                                    7
                                                                                  ? "text-warning"
                                                                                  : "text-success"
                                                                              }`}
                                                                            >
                                                                              {getDaysRemaining(
                                                                                batch.NgaySD
                                                                              ) <
                                                                              0
                                                                                ? "Đã hết hạn"
                                                                                : `${getDaysRemaining(
                                                                                    batch.NgaySD
                                                                                  )} ngày`}
                                                                            </span>
                                                                          ) : (
                                                                            "N/A"
                                                                          )}
                                                                        </td>
                                                                        <td className="text-end">
                                                                          {formatNumber(
                                                                            batch.DonGiaLoSX
                                                                          )}{" "}
                                                                          ₫
                                                                        </td>
                                                                        <td className="text-end">
                                                                          {formatNumber(
                                                                            batch.SoLuongNhap
                                                                          )}
                                                                        </td>
                                                                        <td className="text-end">
                                                                          {formatNumber(
                                                                            batch.SoLuongBan
                                                                          )}
                                                                        </td>
                                                                        <td className="text-end">
                                                                          {formatNumber(
                                                                            store.storeId ===
                                                                              "20"
                                                                              ? batch.SoLuongPhanBo
                                                                              : batch.SoLuongDieuChuyen
                                                                          )}
                                                                        </td>
                                                                        <td className="text-end">
                                                                          {formatNumber(
                                                                            batch.SoLuongTraVe
                                                                          )}
                                                                        </td>
                                                                        <td className="text-end">
                                                                          <span
                                                                            className={`fw-bold ${
                                                                              batch.TonKho <
                                                                              0
                                                                                ? "text-danger"
                                                                                : batch.TonKho >
                                                                                  0
                                                                                ? "text-primary"
                                                                                : "text-muted"
                                                                            }`}
                                                                          >
                                                                            {formatNumber(
                                                                              batch.TonKho
                                                                            )}
                                                                          </span>
                                                                        </td>
                                                                      </tr>
                                                                    )
                                                                  )}
                                                              </tbody>
                                                            </table>
                                                          </div>
                                                        ) : (
                                                          <div className="text-center text-muted py-2">
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            Không có chi tiết lô
                                                            sản xuất
                                                          </div>
                                                        )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                              </React.Fragment>
                                            ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}

                          {/* Single Store Mode: Batch Details */}
                          {!isAllStoresMode &&
                            expandedBatches.has(item.MaSP) && (
                              <tr>
                                <td colSpan="12" className="p-0">
                                  <div className="batch-details bg-light p-3">
                                    <h6 className="text-primary mb-3">
                                      <i className="fas fa-list me-2"></i>
                                      Chi Tiết Theo Lô Sản Xuất
                                    </h6>
                                    {item.batches && item.batches.length > 0 ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm table-striped mb-0">
                                          <thead>
                                            <tr>
                                              <th>Lô SX</th>
                                              <th>Ngày SX</th>
                                              <th>Hạn SD</th>
                                              <th className="text-end">
                                                Hạn SD còn lại
                                              </th>
                                              <th className="text-end">
                                                Đơn Giá
                                              </th>
                                              <th className="text-end">
                                                SL Nhập
                                              </th>
                                              <th className="text-end">
                                                SL Bán
                                              </th>
                                              <th className="text-end">
                                                {maCH === "20"
                                                  ? "SL Phân Bổ"
                                                  : "SL Điều Chuyển"}
                                              </th>
                                              <th className="text-end">
                                                SL Trả Về
                                              </th>
                                              <th className="text-end">
                                                K.Kê Chênh Lệch
                                              </th>
                                              <th className="text-end">
                                                Tồn Kho
                                              </th>
                                              <th className="text-end">
                                                TT Nhập
                                              </th>
                                              <th className="text-end">
                                                TT Bán
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {item.batches
                                              .filter((batch) => {
                                                const days = getDaysRemaining(
                                                  batch.NgaySD
                                                );
                                                if (
                                                  filterNotExpired &&
                                                  days < 0
                                                )
                                                  return false;
                                                if (
                                                  filterExpiringDays !== null &&
                                                  !(
                                                    days >= 0 &&
                                                    days <= filterExpiringDays
                                                  )
                                                )
                                                  return false;
                                                return true;
                                              })
                                              .map((batch, bIndex) => (
                                                <tr key={bIndex}>
                                                  <td>
                                                    <span className="badge bg-info">
                                                      {batch.LoSX}
                                                    </span>
                                                  </td>
                                                  <td>
                                                    {formatDate(batch.NgaySX)}
                                                  </td>
                                                  <td>
                                                    <span
                                                      className={`${
                                                        isExpired(batch.NgaySD)
                                                          ? "text-danger fw-bold"
                                                          : isExpiringSoon(
                                                              batch.NgaySD
                                                            )
                                                          ? "text-warning fw-bold"
                                                          : ""
                                                      }`}
                                                    >
                                                      {formatDate(batch.NgaySD)}
                                                      {isExpired(
                                                        batch.NgaySD
                                                      ) && " (Hết hạn)"}
                                                      {!isExpired(
                                                        batch.NgaySD
                                                      ) &&
                                                        isExpiringSoon(
                                                          batch.NgaySD
                                                        ) &&
                                                        " (Sắp hết hạn)"}
                                                    </span>
                                                  </td>
                                                  <td className="text-end">
                                                    {getDaysRemaining(
                                                      batch.NgaySD
                                                    ) !== null ? (
                                                      <span
                                                        className={`fw-bold ${
                                                          getDaysRemaining(
                                                            batch.NgaySD
                                                          ) < 0
                                                            ? "text-danger"
                                                            : getDaysRemaining(
                                                                batch.NgaySD
                                                              ) <= 7
                                                            ? "text-warning"
                                                            : "text-success"
                                                        }`}
                                                      >
                                                        {getDaysRemaining(
                                                          batch.NgaySD
                                                        ) < 0
                                                          ? "Đã hết hạn"
                                                          : `${getDaysRemaining(
                                                              batch.NgaySD
                                                            )} ngày`}
                                                      </span>
                                                    ) : (
                                                      "N/A"
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      batch.DonGiaLoSX
                                                    )}{" "}
                                                    ₫
                                                  </td>
                                                  <td className="text-end fw-semibold text-info">
                                                    {formatNumber(
                                                      batch.SoLuongNhap
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-success">
                                                    {formatNumber(
                                                      batch.SoLuongBan
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-warning">
                                                    {formatNumber(
                                                      maCH === "20"
                                                        ? batch.SoLuongPhanBo
                                                        : batch.SoLuongDieuChuyen
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-secondary">
                                                    {formatNumber(
                                                      batch.SoLuongTraVe
                                                    )}
                                                  </td>
                                                  <td className="text-end fw-semibold text-info">
                                                    {formatNumber(
                                                      batch.SoLuongKiemKe
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    <span
                                                      className={`fw-bold ${
                                                        batch.TonKho < 0
                                                          ? "text-danger"
                                                          : batch.TonKho > 0
                                                          ? "text-primary"
                                                          : "text-muted"
                                                      }`}
                                                    >
                                                      {formatNumber(
                                                        batch.TonKho
                                                      )}
                                                    </span>
                                                  </td>
                                                  <td className="text-end fw-semibold text-info">
                                                    {formatNumber(
                                                      batch.ThanhTienNhap
                                                    )}{" "}
                                                    ₫
                                                  </td>
                                                  <td className="text-end fw-semibold text-success">
                                                    {formatNumber(
                                                      batch.ThanhTienBan
                                                    )}{" "}
                                                    ₫
                                                  </td>
                                                </tr>
                                              ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-center text-muted py-3">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Không có chi tiết lô sản xuất
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={isAllStoresMode ? "13" : "12"}
                          className="text-center"
                        >
                          <div className="empty-state">
                            <i
                              className="fas fa-inbox"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <div className="mt-2">
                              <strong>
                                {productSearchTerm ||
                                filterInStock ||
                                filterNotExpired
                                  ? "Không tìm thấy sản phẩm phù hợp"
                                  : "Không có dữ liệu"}
                              </strong>
                              <br />
                              <small>
                                {productSearchTerm ||
                                filterInStock ||
                                filterNotExpired
                                  ? "Hãy thử thay đổi điều kiện tìm kiếm hoặc bộ lọc"
                                  : "Hãy thử thay đổi bộ lọc"}
                              </small>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Export Button */}
        {getFilteredInventoryData().length > 0 && (
          <div className="export-section">
            <div className="d-flex gap-3 flex-wrap">
              <button
                className="btn btn-success btn-lg"
                onClick={exportToExcel}
              >
                <i className="fas fa-file-excel me-2"></i>
                Xuất Excel Tồn Kho
              </button>

              {/* Add new export buttons */}
              <button
                className="btn btn-info btn-lg"
                onClick={() => exportSalesReceiptsToExcel(false)}
                disabled={loading || isAllStoresMode}
                title={
                  isAllStoresMode
                    ? "Không khả dụng ở chế độ tất cả cửa hàng"
                    : "Xuất chi tiết hóa đơn bán hàng của cửa hàng hiện tại"
                }
              >
                <i className="fas fa-receipt me-2"></i>
                Xuất Hóa Đơn Bán Hàng
              </button>

              <button
                className="btn btn-primary btn-lg"
                onClick={() => exportSalesReceiptsToExcel(true)}
                disabled={loading}
                title="Xuất chi tiết hóa đơn bán hàng của tất cả cửa hàng Bakery"
              >
                <i className="fas fa-store me-2"></i>
                Xuất Hóa Đơn Tất Cả Cửa Hàng
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  </>
  );
};

export default DSTrungThu;
